import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  PreferenceDto,
  PreferenceListDto,
  UpsertPreferenceBodyDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';

export const preferenceKeys = {
  pinnedOrganizations: 'extension-pinned-organizations',
  tableSettings: 'table-settings',
};

export enum SpendBrainTableName {
  Documents = 'documents',
  VirtualDocuments = 'virtual-documents',
  SibLocations = 'sib-locations',
  VectorLocations = 'vector-locations',
  Tickets = 'tickets',
  Contracts = 'contracts',
  InvoiceDetails = 'invoice-details',
  SubsidiaryOrganizations = 'subsidiary-organizations',
  BillingSummary = 'billing-summary',
  Savings = 'savings',
  Vendors = 'vendors',
}

export enum SidecarTableName {
  Organizations = 'organizations',
  OrganizationSubsidiaryOrganizations = 'organization-subsidiary-organizations',
  OrganizationLocations = 'organization-locations',
  OrganizationActionItems = 'organization-action-items',
  OrganizationLocationsImportDuplicatesCompact = 'organization-locations-import-duplicates-compact',
  OrganizationDocuments = 'organization-documents',
  OrganizationVendors = 'organization-vendors',
  Vendors = 'vendors',
  VendorContacts = 'vendor-contacts',
  VendorTickets = 'vendor-tickets',
  VendorContracts = 'vendor-contracts',
  ProjectTasks = 'project-tasks',
  ProjectFiles = 'project-files',
  ProjectTickets = 'project-tickets',
  VendorTasks = 'vendor-tasks',
  VendorsInvalidImport = 'vendors-invalid-import',
}

export interface TableColumnPreference {
  enabled?: boolean;
  visible?: boolean;
  pinned?: 'left' | 'right' | false;
}

export type TableSettings = Partial<
  Record<SpendBrainTableName, { [key: string]: TableColumnPreference }>
> &
  Partial<Record<SidecarTableName, { [key: string]: TableColumnPreference }>>;

export const useGetPreferences = (
  query: Parameters<
    typeof api.preference.preferenceControllerGetPreferences
  >[0],
  options?: UseQueryOptions<PreferenceListDto, Error, PreferenceListDto>
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.UserPreferences,
      query?.type,
      query?.organizationId,
    ],
    queryFn: async () =>
      api.preference.preferenceControllerGetPreferences(query),
  });

interface UpsertPreferencesParams {
  key: string;
  query: Parameters<
    typeof api.preference.preferenceControllerUpsertPreferences
  >[1];
  data: UpsertPreferenceBodyDto;
}

export const useUpdatePreferences = (
  options?: UseMutationOptions<PreferenceDto, Error, UpsertPreferencesParams>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.UserPreferences],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ key, query, data }) =>
      api.preference.preferenceControllerUpsertPreferences(key, query, data),
  });
};
